import { sharedPostPreview } from '../ui/19-post-sharing.mjs';
import { createDirectMessages } from '../ui/14-direct-messages.mjs';
import { createReactionControl } from '../ui/08-reactions.mjs';
// Owns conversations rendering and interactions.
// Dependencies are injected by app.mjs; feature modules never import one another.
/** Create circle, message and notification views backed by server commands. */
export function createConversations(ctx) {
  let draftActor;
  const drafts = new Map();
  let activeCircle;
  function bindDraft(input, key) {
    if (draftActor !== ctx.state.me.id) {
      drafts.clear();
      draftActor = ctx.state.me.id;
    }
    key = `${draftActor}:${key}`;
    input.value = drafts.get(key) ?? '';
    input.addEventListener('input', () => drafts.set(key, input.value));
    return key;
  }

  function renderCircles(id) {
    activeCircle = null;
    ctx.view.dataset.experience = 'circles';
    const groups = ctx.state.groups ?? [];
    const joined = groups.filter((g) => !g.invited);
    const group = joined.find((g) => g.id === id);
    if (id && !group) {
      ctx.view.append(ctx.empty('Circle unavailable.'), circleBack());
      return;
    }
    if (group) {
      renderCircleChat(group);
      return;
    }
    ctx.view.append(
      ctx.append(
        ctx.e('div', 'circle-toolbar'),
        ctx.e('h1', '', 'Circles'),
        ctx.iconButton('plus', 'Create a circle', createCircle),
      ),
    );
    for (const g of groups.filter((g) => g.invited)) {
      ctx.view.append(
        ctx.append(
          ctx.e('section', 'settings-card'),
          ctx.e('h2', '', g.name),
          ctx.e('p', '', `${ctx.who(g.invitedBy ?? g.ownerId)?.name ?? 'A member'} invited you.`),
          ctx.button('Accept', () =>
            ctx.command({ type: 'group-invite', id: g.id, action: 'accept' }),
          ),
          ctx.button('Decline', () =>
            ctx.command({ type: 'group-invite', id: g.id, action: 'decline' }),
          ),
        ),
      );
    }
    if (!joined.length) {
      ctx.view.append(ctx.empty('No circles yet. Create one and invite your friends.'));
      return;
    }
    const choices = ctx.e('nav', 'conversation-list circle-choices');
    choices.setAttribute('aria-label', ctx.uiText('Your circles'));
    for (const g of joined) {
      const choice = ctx.link('', `#/circles/${g.id}`);
      choice.dataset.focusKey = `circle-${g.id}`;
      choice.append(
        ctx.icon('users'),
        ctx.append(
          ctx.e('div'),
          ctx.e('strong', '', g.name),
          ctx.e(
            'small',
            '',
            g.messages.at(-1)?.text.slice(0, 90) || ctx.uiText('Start a conversation'),
          ),
        ),
        ctx.icon('chevron-right'),
      );
      choices.append(choice);
    }
    ctx.view.append(choices);
  }

  function circleBack() {
    return ctx.iconButton('arrow-left', 'Back to circles', () => ctx.go('circles'));
  }

  function messageRow(message, groupId) {
    const sender = ctx.who(message.authorId)?.name ?? ctx.uiText('Member');
    const row = ctx.e('article', 'message' + (message.authorId === ctx.state.me.id ? ' own' : ''));
    row.setAttribute('aria-label', `${ctx.uiText('Message from')} ${sender}`);
    if (groupId) row.append(ctx.e('strong', 'message-author', sender));
    row.append(ctx.e('p', 'message-text', message.text));
    const shared = sharedPostPreview(ctx, message);
    if (shared) row.append(shared);
    row.append(ctx.e('small', 'message-time', ctx.time(message.createdAt)));
    const reaction = createReactionControl(ctx, {
      record: message,
      current: () =>
        groupId
          ? ctx.state.groups
              .find((group) => group.id === groupId)
              ?.messages.find((item) => item.id === message.id)
          : ctx.state.messages.find((item) => item.id === message.id),
      command: groupId
        ? { type: 'group-reaction', id: groupId, messageId: message.id }
        : { type: 'message-reaction', id: message.id },
      key: `message-${message.id}`,
    });
    reaction.classList.add('message-border-reaction');
    row.append(reaction);
    return row;
  }

  function renderCircleChat(group) {
    const conversation = ctx.e('section', 'conversation circle-conversation');
    const info = ctx.iconButton('info', 'Circle info', () => circleInformation(group.id));
    info.dataset.focusKey = `circle-info-${group.id}`;
    const title = ctx.e('h1', '', group.name);
    const count = ctx.e('small', 'subtle', `${group.members.length} ${ctx.uiText('Members')}`);
    const actions = ctx.e('div', 'circle-header-actions');
    let addPeople;
    if (group.adminIds?.includes(ctx.state.me.id)) {
      const add = ctx.iconButton('user-plus', 'Add people', () => invitePeople(group.id));
      add.dataset.focusKey = `circle-add-${group.id}`;
      addPeople = add;
      actions.append(add);
    }
    actions.append(info);
    activeCircle = { id: group.id, title, count, addPeople };
    conversation.append(
      ctx.append(
        ctx.e('header', 'circle-header'),
        circleBack(),
        ctx.append(ctx.e('div'), title, count),
        actions,
      ),
    );
    const messages = ctx.e('div', 'message-list circle-transcript');
    messages.tabIndex = 0;
    messages.setAttribute('aria-label', ctx.uiText('Circle conversation'));
    if (!group.messages.length) messages.append(ctx.empty('Say hello to your circle.'));
    for (const message of group.messages) messages.append(messageRow(message, group.id));
    const form = ctx.e('form', 'comment-form');
    const input = ctx.field('input', {
      required: true,
      maxLength: 2000,
      placeholder: 'Message your circle',
      ariaLabel: 'Circle message',
    });
    const draftKey = bindDraft(input, `circle:${group.id}`);
    const send = ctx.e('button', 'primary-button');
    send.append(ctx.icon('send'));
    send.setAttribute('aria-label', ctx.uiText('Send circle message'));
    send.title = ctx.uiText('Send circle message');
    form.append(input, send);
    let pending = false;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending) return;
      pending = true;
      send.setAttribute('aria-busy', 'true');
      const sentText = input.value;
      const known = new Set(
        (ctx.state.groups.find((g) => g.id === group.id)?.messages ?? []).map((m) => m.id),
      );
      try {
        const snapshot = await ctx.command(
          { type: 'group-message', id: group.id, text: sentText },
          { paint: false },
        );
        const latest =
          snapshot?.groups.find((g) => g.id === group.id) ??
          ctx.state.groups.find((g) => g.id === group.id);
        if (!known.size) messages.replaceChildren();
        for (const message of latest.messages.filter((m) => !known.has(m.id))) {
          const row = messageRow(message, group.id);
          row.classList.add('message-enter');
          messages.append(row);
        }
        if (input.value === sentText) {
          input.value = '';
          drafts.delete(draftKey);
        }
        messages.scrollTop = messages.scrollHeight;
        input.focus();
      } catch (error) {
        ctx.toast(error.message);
      } finally {
        pending = false;
        send.removeAttribute('aria-busy');
      }
    });
    conversation.append(messages, form);
    ctx.view.append(conversation);
  }

  function circleInformation(id, nameDraft) {
    const group = ctx.state.groups.find((g) => g.id === id && !g.invited);
    if (!group) return;
    const admin = group.adminIds?.includes(ctx.state.me.id);
    const content = ctx.e('div', 'form-stack');
    content.append(ctx.e('p', 'subtle', 'Local messages are not end-to-end encrypted.'));
    content.append(
      ctx.e(
        'p',
        'subtle',
        ctx.uiText(
          admin
            ? 'You are a circle admin.'
            : 'Only circle admins can add people or change this circle.',
        ),
      ),
    );
    if (admin)
      content.append(
        ctx.iconButton('user-plus', 'Add people', () => {
          const name = content.querySelector('input');
          invitePeople(id, name?.value);
        }),
      );
    const mutate = async (input) => {
      const focused = document.activeElement?.dataset.focusKey;
      const nameInput = document.querySelector('#modal-body input');
      await ctx.command({ id, ...input }, { paint: false });
      const updated = ctx.state.groups.find((g) => g.id === id);
      if (activeCircle?.id === id && updated) {
        if (activeCircle.addPeople)
          activeCircle.addPeople.hidden = !updated.adminIds.includes(ctx.state.me.id);
        activeCircle.title.textContent = updated.name;
        activeCircle.count.textContent = `${updated.members.length} ${ctx.uiText('Members')}`;
      }
      circleInformation(id, input.type === 'group-update' ? undefined : nameInput?.value);
      if (focused) {
        const replacement = [...document.querySelectorAll('[data-focus-key]')].find(
          (node) => node.dataset.focusKey === focused,
        );
        const target =
          replacement ??
          document.querySelector('#modal-body input, #modal-body button, #modal button');
        target?.focus({ preventScroll: true });
      }
    };
    const actionIcon = (icon, label, input, key) => {
      const button = ctx.iconButton(icon, label, () => mutate(input));
      button.dataset.focusKey = `circle-${id}-${key}`;
      return button;
    };
    if (admin) {
      const form = ctx.e('form', 'comment-form');
      const name = ctx.field('input', {
        value: nameDraft ?? group.name,
        required: true,
        maxLength: 80,
        ariaLabel: 'Circle name',
      });
      name.dataset.focusKey = `circle-name-${id}`;
      const save = ctx.e('button', 'primary-button');
      save.dataset.focusKey = `circle-save-${id}`;
      save.append(ctx.icon('check'));
      save.setAttribute('aria-label', ctx.uiText('Update circle name'));
      save.title = ctx.uiText('Update circle name');
      let pending = false;
      form.append(name, save);
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (pending) return;
        pending = true;
        try {
          await mutate({ type: 'group-update', name: name.value });
        } catch (error) {
          ctx.toast(error.message);
          pending = false;
        }
      });
      content.append(form);
    }
    content.append(ctx.e('h2', '', 'Members'));
    for (const memberId of group.members) {
      const member = ctx.who(memberId);
      const isAdmin = group.adminIds?.includes(memberId);
      const row = ctx.append(
        ctx.e('div', 'circle-member'),
        ctx.avatar(member),
        ctx.e('span', '', member?.name ?? ctx.uiText('Member')),
      );
      if (isAdmin) row.append(ctx.e('small', 'subtle', 'Admin'));
      if (admin) {
        row.append(
          actionIcon(
            isAdmin ? 'shield-off' : 'shield-check',
            isAdmin ? 'Remove admin role' : 'Make admin',
            { type: 'group-admin', memberId, admin: !isAdmin },
            `role-${memberId}`,
          ),
        );
        if (memberId !== ctx.state.me.id)
          row.append(
            actionIcon(
              'user-minus',
              'Remove member',
              { type: 'group-remove', memberId },
              `remove-${memberId}`,
            ),
          );
      }
      content.append(row);
    }
    if (admin)
      content.append(ctx.e('p', 'subtle', 'Assign another admin before the last admin leaves.'));
    content.append(
      ctx.button(
        'Leave circle',
        async () => {
          await ctx.command({ type: 'group-leave', id }, { paint: false });
          ctx.modal.close();
          ctx.go('circles');
        },
        'text-button',
      ),
    );
    ctx.dialog('Circle info', content);
  }

  function invitePeople(id, nameDraft) {
    const group = ctx.state.groups.find((g) => g.id === id && !g.invited);
    if (!group?.adminIds?.includes(ctx.state.me.id)) {
      ctx.toast(ctx.uiText('Only circle admins can add people or change this circle.'));
      return;
    }
    const content = ctx.e('div', 'form-stack');
    content.append(
      ctx.iconButton('arrow-left', 'Back to circle info', () => circleInformation(id, nameDraft)),
      ctx.e('p', 'subtle', ctx.uiText('Invite friends. They must accept before joining.')),
      ctx.e('p', 'subtle', ctx.uiText('New members can read existing circle messages.')),
    );
    const search = ctx.field('input', {
      type: 'search',
      placeholder: ctx.uiText('Search friends'),
      ariaLabel: ctx.uiText('Search friends'),
      maxLength: 120,
    });
    search.value = '';
    const results = ctx.e('div', 'circle-invite-results');
    results.setAttribute('aria-live', 'polite');
    const paint = () => {
      const latest = ctx.state.groups.find((g) => g.id === id);
      results.replaceChildren();
      if (!latest?.adminIds?.includes(ctx.state.me.id)) {
        results.append(
          ctx.e(
            'p',
            'subtle',
            ctx.uiText('Only circle admins can add people or change this circle.'),
          ),
        );
        return;
      }
      const friends = ctx.state.friends.filter(
        (friend) => !ctx.state.settings.blocked.includes(friend),
      );
      const matching = friends.filter((friend) =>
        (ctx.who(friend)?.name ?? '')
          .toLocaleLowerCase()
          .includes(search.value.trim().toLocaleLowerCase()),
      );
      if (!matching.length) {
        results.append(
          ctx.empty(
            ctx.uiText(
              friends.length ? 'No friends match this search.' : 'No friends to invite yet.',
            ),
          ),
        );
      }
      for (const memberId of matching) {
        const member = ctx.who(memberId);
        const row = ctx.append(
          ctx.e('div', 'circle-member'),
          ctx.avatar(member),
          ctx.e('span', '', member.name),
        );
        if (latest.members.includes(memberId))
          row.append(ctx.e('small', 'subtle', ctx.uiText('Already a member')));
        else if (
          latest.invites.some((invite) => invite.userId === memberId && invite.status === 'pending')
        )
          row.append(ctx.e('small', 'subtle', ctx.uiText('Invited')));
        else {
          const invite = ctx.iconButton('user-plus', 'Invite friend', async () => {
            await ctx.command({ type: 'group-add', id, memberId }, { paint: false });
            paint();
            search.focus({ preventScroll: true });
          });
          invite.dataset.focusKey = `circle-invite-${id}-${memberId}`;
          row.append(invite);
        }
        results.append(row);
      }
    };
    search.addEventListener('input', paint);
    const findPeople = ctx.link(ctx.uiText('Find people'), '#/discover', 'text-button');
    content.append(search, results, findPeople);
    paint();
    ctx.dialog('Add people', content);
    search.focus({ preventScroll: true });
  }

  function createCircle() {
    const form = ctx.e('form', 'form-stack'),
      name = ctx.field('input', {
        required: true,
        maxLength: 80,
        placeholder: 'Weekend plans, Book club…',
        ariaLabel: 'Circle name',
      });
    form.append(
      name,
      ctx.e('p', 'subtle', 'Friends receive an invitation. They must accept before joining.'),
    );
    const members = ctx.e('div', 'tag-grid');
    for (const id of ctx.state.friends.filter((id) => !ctx.state.settings.blocked.includes(id))) {
      const check = ctx.field('input', { type: 'checkbox', value: id });
      members.append(
        ctx.append(
          ctx.e('label', 'tag-choice'),
          check,
          ctx.avatar(ctx.who(id)),
          ctx.e('span', '', ctx.who(id).name),
        ),
      );
    }
    form.append(members);
    const save = ctx.e('button', 'primary-button', 'Create circle');
    form.append(save);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      save.disabled = true;
      try {
        await ctx.command(
          {
            type: 'group-create',
            name: name.value,
            memberIds: [...members.querySelectorAll('input:checked')].map((n) => n.value),
          },
          { paint: false },
        );
        ctx.modal.close();
        ctx.go('circles');
      } catch (error) {
        ctx.toast(error.message);
        save.disabled = false;
      }
    });
    ctx.dialog('Create a private circle', form);
  }

  function renderNotifications() {
    ctx.view.append(
      ctx.heading('A little connection.', 'Replies, reactions, and updates from your people.'),
      ctx.button('Mark all as read', () => ctx.command({ type: 'read-notifications' })),
    );
    if (!ctx.state.notifications.length) ctx.view.append(ctx.empty('No notifications yet.'));
    [...ctx.state.notifications]
      .sort((a, b) => b.createdAt - a.createdAt)
      .forEach((n) => {
        const row = ctx.link(
          '',
          n.conversationId
            ? `#/messages/${n.conversationId}`
            : n.postId
              ? `#/post/${n.postId}`
              : `#/profile/${n.actorId}`,
          `notification${n.read ? '' : ' unread'}`,
        );
        row.append(
          ctx.avatar(ctx.who(n.actorId)),
          ctx.append(
            ctx.e('div'),
            ctx.e('p', '', n.text),
            ctx.e('small', '', ctx.time(n.createdAt)),
          ),
        );
        ctx.view.append(row);
      });
  }

  const { renderMessages } = createDirectMessages(ctx, { bindDraft, drafts, messageRow });
  return { renderCircles, createCircle, renderNotifications, renderMessages };
}
